import Modal from "@/components/atoms/Modal";
import Select, { SelectPaginateAsync } from "@/components/atoms/Select";
import useLoadOptions from "@/hooks/useLoadOptions";
import { CompanyUserForm, USER_COMPANY_ROLES } from "@/interfaces/CompanyUser";
import { UserFilters } from "@/interfaces/IUser";
import { IModalMethods } from "@/interfaces/Modal";
import { useStoreCompanyUserMutation } from "@/services/companies/users";
import {
  ElementRef,
  ForwardRefRenderFunction,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { Col, Form, Row } from "react-bootstrap";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";

const StoreCompanyUserModal: ForwardRefRenderFunction<
  IModalMethods,
  { companyId: string }
> = ({ companyId }, ref) => {
  const ModalRef = useRef<ElementRef<typeof Modal>>(null);
  const { control, handleSubmit } = useForm<CompanyUserForm>({
    defaultValues: {
      role: USER_COMPANY_ROLES[0],
      commission: 7.5,
    },
  });

  const { loadUsersOptions } = useLoadOptions();

  const defaultAdditional = useRef<UserFilters>({
    pageIndex: 0,
    isActive: true,
  }).current;

  const [storeCompanyUsersModal] = useStoreCompanyUserMutation();

  const showOrHiddenModal = () => ModalRef.current?.showOrHiddenModal();

  useImperativeHandle(ref, () => ({
    showOrHiddenModal,
  }));

  const onSubmit: SubmitHandler<CompanyUserForm> = async ({
    user,
    role,
    ...values
  }) => {
    return await toast.promise(
      storeCompanyUsersModal([
        companyId,
        {
          userId: user!.id,
          role: role.value,
          ...values,
        },
      ]).unwrap(),
      {
        pending: "Ajout de l'utilisateur en cours...",
        success: {
          render() {
            showOrHiddenModal();
            return (
              <p style={{ marginBottom: 0, textAlign: "center" }}>
                Les utilisateurs ont bien été ajoutés à l'entreprise 🚀
              </p>
            );
          },
        },
        error: {
          render() {
            return "Une erreur est survenue";
          },
        },
      },
    );
  };

  return (
    <Modal
      ref={ModalRef}
      title="Ajouter un utilisateur"
      requiredLabel
      onSubmit={handleSubmit(onSubmit)}
    >
      <Row className="gx-3">
        <Col lg={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Utilisateur <span className="text-danger">*</span> :
            </Form.Label>
            <Controller
              control={control}
              name="user"
              rules={{ required: true }}
              render={({ field }) => (
                <SelectPaginateAsync
                  loadOptions={loadUsersOptions}
                  getOptionLabel={(option) => option.fullname}
                  getOptionValue={(option) => option.id}
                  value={field.value}
                  onChange={field.onChange}
                  additional={defaultAdditional}
                />
              )}
            />
          </Form.Group>
        </Col>
        <Col lg={6}>
          <Controller
            control={control}
            name="role"
            rules={{ required: true }}
            render={({ field }) => (
              <Form.Group className="mb-3">
                <Form.Label>
                  Role <span className="text-danger">*</span> :
                </Form.Label>
                <Select
                  options={USER_COMPANY_ROLES}
                  value={field.value}
                  onChange={field.onChange}
                />
              </Form.Group>
            )}
          />
        </Col>
      </Row>
    </Modal>
  );
};

export default forwardRef(StoreCompanyUserModal);
